import React, { forwardRef, useCallback } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../../../stores/app'
import { BASE_CLASSNAME } from '../constants'
import { NotificationBuilderViewType, OrgNotificationBuilderViewProps } from '../types'
import classnames from 'classnames'
import { PageHeader } from '@pushly/aqe/lib/components'
import NotificationBuilder, { NotificationBuilderRef } from '../../../notification-builder'
import { NotificationBuilderViewRightAside } from './notification-builder-view-right-aside'
import { NotificationBuilderViewMain } from './notification-builder-view-main'
import { NotificationBuilderLevel, NotificationBuilderTheme } from '../../../enums'
import { AppService } from '../../../../../services'
import { useLoadedOrgNotification } from '../../../../../providers/org-notification-provider/core'
import { ErrorPage } from '../../../../error-page/error-page'
import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'
import { useOrgProvider } from '../../../../../providers/org-provider/core'
import { getAccountEnabledDeliveryChannels } from '../../../../../_utils/account'

type OrgNotificationBuilderViewInternalProps = OrgNotificationBuilderViewProps & {
    levelId: number
    requestedNotifId?: number
    requestedTemplateId?: number
}

export const OrgNotificationBuilderViewInternals = forwardRef<
    NotificationBuilderRef,
    OrgNotificationBuilderViewInternalProps
>((props, ref) => {
    const {
        className,
        formMode = 'create',
        requestedNotifId,
        requestedTemplateId,
        hidePageHeader = false,
        hideEstimatedReach = false,
        hidePreview = false,
        hideDevicePreviewAction = false,
        hideInlinePreviewAction = false,
        hideDeliveryChannels = false,
        hideAudienceSelection = false,
        hideScheduling = false,
        hideSubmit = false,
        hidePushlyAiToggle = false,
        hidePageAnalysisToggle = false,
        showTemplateDetails = false,
        nestedDeliveryChannels = false,
        getParentRoute,
        onSubmit,
        onCancel,
        submitText,
        cancelText,
        builderProps,
    } = props

    const appState = useService(AppState)
    const appSvc = useService(AppService)

    const [canEdit, setCanEdit] = React.useState(true)
    const handleOrgNotificationAuthZError = useCallback(() => {
        setCanEdit(false)
    }, [setCanEdit])

    const orgApi = useOrgProvider()
    const loadedNotifApi = useLoadedOrgNotification({
        onAuthZError: handleOrgNotificationAuthZError,
    })

    /*
    ORG controlled props
    */
    const level: PlatformLevel = 'org'
    const formType: NotificationBuilderViewType = 'send'
    const isInDrawer = false
    const isMobile = false
    const journey = undefined
    const journeyNotificationType = undefined
    const hideTestConfig = true
    const hideTemplateSelector = true
    const hideVariantManager = true
    const readonlyVariantManager = true
    const catalogItemPick = false
    /* --- */

    const org = orgApi.getOrg()
    const orgDomains = orgApi.getDomains()
    const orgActiveDeliveryChannels = getAccountEnabledDeliveryChannels(org)

    // ORG level currently defaults to the first domain in the org
    const domain = orgDomains[0] ?? appState.currentDomain!
    const isEditMode = formMode === 'edit'

    const builderTheme = NotificationBuilderTheme.STANDARD
    const builderType = 'notification'
    const builderLevel = NotificationBuilderLevel.ORG

    const isRemoteLoading = loadedNotifApi.isLoading()
    const loadedNotifsPromise = loadedNotifApi.getNotificationsPromise()

    const pageTitle = isEditMode ? 'Edit Multi-Domain Notification' : 'Create Multi-Domain Notification'
    const pageTitleAppend = isEditMode && requestedNotifId ? `#${requestedNotifId}` : undefined

    if (!canEdit) {
        return (
            <ErrorPage
                errorCode={403}
                label="You do not have sufficient permissions to edit this notification"
                hideLogo={true}
                hideBackgroundCode={true}
            />
        )
    }

    return (
        <div
            className={classnames(
                BASE_CLASSNAME,
                `${BASE_CLASSNAME}__level-${level}`,
                isMobile && `${BASE_CLASSNAME}__mobile`,
                isRemoteLoading && `${BASE_CLASSNAME}__loading`,
                className,
            )}
        >
            {!hidePageHeader && (
                <PageHeader title={pageTitle} append={pageTitleAppend} onTitleSet={appSvc.customizeTabTitle} />
            )}

            <NotificationBuilder
                {...builderProps}
                ref={ref}
                className={classnames(isInDrawer && 'drawer-mode', builderProps?.className)}
                mode={formMode}
                type={builderType}
                theme={builderTheme}
                domain={domain}
                account={org}
                level={builderLevel}
                notifications={builderProps?.notifications ?? loadedNotifsPromise}
                defaultSelectedNotifId={requestedNotifId ?? requestedTemplateId}
                defaultSelectedTemplateId={requestedTemplateId}
                defaultChannels={orgActiveDeliveryChannels}
            >
                {({
                    builder,
                    dispatchChanges,
                    onInitialVariantInit,
                    validateSubmit,
                    onChannelChange,
                    onTestChange,
                    onAudienceChange,
                    onDistributionChange,
                    onVariantChange,
                    onSelectedTemplateChange,
                    onDetailsChange,
                }) => {
                    return (
                        <>
                            <NotificationBuilderViewRightAside
                                loading={isRemoteLoading}
                                level={level}
                                domain={domain}
                                org={org}
                                orgDomains={orgDomains}
                                builder={builder}
                                hideEstimatedReach={hideEstimatedReach}
                                hidePreview={hidePreview}
                                hideDevicePreviewAction={hideDevicePreviewAction}
                                hideInlinePreviewAction={hideInlinePreviewAction}
                                isInDrawer={isInDrawer}
                                dispatchChanges={dispatchChanges}
                                onDistributionChange={onDistributionChange}
                                validateSubmit={validateSubmit}
                                journey={journey}
                                journeyNotificationType={journeyNotificationType}
                            />

                            <NotificationBuilderViewMain
                                level={level}
                                formType={formType}
                                formMode={formMode}
                                loading={isRemoteLoading}
                                domain={domain}
                                org={org}
                                builder={builder}
                                builderProps={builderProps}
                                isMobile={isMobile}
                                isInDrawer={isInDrawer}
                                requestedNotifId={requestedNotifId}
                                hideDeliveryChannels={hideDeliveryChannels}
                                hideAudienceSelection={hideAudienceSelection}
                                hideTestConfig={hideTestConfig}
                                hideTemplateSelector={
                                    loadedNotifApi.hasRequestedNotificationId() || hideTemplateSelector
                                }
                                hideVariantManager={hideVariantManager}
                                hideScheduling={hideScheduling}
                                hideSubmit={hideSubmit}
                                hidePushlyAiToggle={hidePushlyAiToggle}
                                hidePageAnalysisToggle={hidePageAnalysisToggle}
                                readonlyVariantManager={readonlyVariantManager}
                                showTemplateDetails={showTemplateDetails}
                                nestedDeliveryChannels={nestedDeliveryChannels}
                                dispatchChanges={dispatchChanges}
                                getParentRoute={getParentRoute}
                                onTemplateChange={onSelectedTemplateChange}
                                onVariantChange={onVariantChange}
                                onChannelChange={onChannelChange}
                                onAudienceChange={onAudienceChange}
                                onTestChange={onTestChange}
                                onInitialVariantInit={onInitialVariantInit}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                onTemplateDetailsChange={onDetailsChange}
                                submitText={submitText}
                                cancelText={cancelText}
                                catalogItemPick={catalogItemPick}
                                validateSubmit={validateSubmit}
                            />
                        </>
                    )
                }}
            </NotificationBuilder>
        </div>
    )
})
